import classes from "./Device.module.css"
import {CircularProgress, Divider, IconButton, Tooltip} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import Record from "./Record";
import React, {useEffect, useState, useMemo} from "react";
import axios from "axios";
import {path} from "../../../../routes";
import EditDisplay_modal from "../../Modal/EditDisplay_modal";

// Parse API date strictly as LOCAL time (no timezone conversion).
// Supported formats:
//   - "YYYY-MM-DD HH:mm:ss"
//   - "YYYY-MM-DDTHH:mm:ss"
//   - with optional milliseconds / timezone suffix (which are ignored)
const parseDate = (v) => {
    if (v == null) return NaN;
    if (typeof v === 'number' && !isNaN(v)) return v;
    const s = String(v).trim();
    if (!s) return NaN;

    // Remove milliseconds and timezone info if present
    let clean = s
        .replace('T', ' ')
        .replace(/\.\d+$/, '')         // drop .mmm
        .replace(/[+-]\d{2}:?\d{2}$/, '') // drop +01:00 / -0500
        .replace(/Z$/, '');

    const [datePart, timePart] = clean.split(' ');
    if (!datePart || !timePart) return NaN;

    const [yStr, mStr, dStr] = datePart.split('-');
    const [hStr, minStr, secStr] = timePart.split(':');
    const y = Number(yStr);
    const m = Number(mStr);
    const d = Number(dStr);
    const h = Number(hStr);
    const min = Number(minStr);
    const sec = Number(secStr || 0);
    if (![y, m, d, h, min, sec].every((n) => Number.isFinite(n))) return NaN;

    // Construct Date in local time zone
    return new Date(y, m - 1, d, h, min, sec).getTime();
};

const formatTimeLocal = (ts) => {
    if (ts == null || isNaN(ts)) return '--:--';
    const d = new Date(ts);
    const h = String(d.getHours()).padStart(2, '0');
    const m = String(d.getMinutes()).padStart(2, '0');
    return `${h}:${m}`;
};

const Device =(props) =>{
    const [name, setName] = useState(props.name)
    const [id, setId] = useState(props.id)
    const [isLoading, setisLoading] = useState(true)
    const [records,setRecords] = useState(
        []
    )
    const [currentTime, setCurrentTime] = useState(new Date())
    const [settingsModalOpen, setSettingsModalOpen] = useState(false)
    const fetchDataDevice = async (day) => {
        if (!day || props.id == null) return;
        setisLoading(true);
        try {
            const response = await axios.get(`${path}/getRecords/${day}/${props.id}`);
            const data = Array.isArray(response.data) ? response.data : [];
            setRecords(data);
        } catch (error) {
            console.error('Error loading records:', error);
            setRecords([]);
        } finally {
            setisLoading(false);
        }
    }

    const update_delete_handler = (x) =>{
        props.update(x)
    }

    useEffect(()=>{
        const day = new Date()
        const yyyy = day.getFullYear()
        const mm = String(day.getMonth() + 1).padStart(2,"0")
        const dd = String(day.getDate()).padStart(2,"0")
        const today = `${yyyy}-${mm}-${dd}`
        fetchDataDevice(props.day)
    },[props.day,props.count])

    // Update current time every 1s – „In progress“ se mění hned při začátku/konci meetingu
    useEffect(() => {
        const tick = () => setCurrentTime(new Date());
        tick(); // immediate
        const interval = setInterval(tick, 1000);
        return () => clearInterval(interval);
    }, [])

    const handleDeviceClick = (e) => {
        e.stopPropagation(); // Prevent event bubbling
        if (props.onDeviceClick) {
            props.onDeviceClick(props.id);
        }
    }

    // Recalculate hasActiveMeeting whenever currentTime or records change
    const hasActiveMeeting = useMemo(() => {
        const now = currentTime.getTime();
        return records.some((x) => {
            const from = parseDate(x.dateFrom);
            const to = parseDate(x.dateTo);
            return !isNaN(from) && !isNaN(to) && from <= now && now <= to;
        });
    }, [records, currentTime]);

    const displayForSettings = props.displayData || { id: props.id, description: name, device_group: 1, cOrganization_id: props.orgId };

    if (props.viewMode === 'list') {
        return (
            <div className={`${classes.main} ${classes.mainList} ${hasActiveMeeting ? classes.hasActiveMeeting : ''}`}>
                <div className={classes.listRow}>
                    <div className={classes.listRoomName} onClick={handleDeviceClick}>
                        {name}
                    </div>
                    <div className={classes.listMeetings}>
                        {records.length === 0 && <span className={classes.listEmpty}>No meetings</span>}
                        {records.slice(0, 3).map((x, idx) => {
                            const from = parseDate(x.dateFrom);
                            const to = parseDate(x.dateTo);
                            const now = currentTime.getTime();
                            const isRunning = !isNaN(from) && !isNaN(to) && from <= now && now <= to;
                            return (
                                <button
                                    key={x.recordID || x.id || idx}
                                    className={`${classes.listMeetingChip} ${isRunning ? classes.listMeetingChipRunning : ''}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        props.update(x);
                                    }}
                                >
                                    {formatTimeLocal(from)}-{formatTimeLocal(to)} {x.recordDescription || x.description}
                                </button>
                            );
                        })}
                        {records.length > 3 && <span className={classes.listMore}>+{records.length - 3} more</span>}
                    </div>
                    <div className={classes.listActions}>
                        <button
                            type="button"
                            className={classes.listPlannerBtn}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (props.onOpenPlanner) props.onOpenPlanner({ id: props.id, description: name });
                            }}
                        >
                            View plan
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return <div className={`${classes.main} ${props.viewMode === 'list' ? classes.mainList : ''} ${hasActiveMeeting ? classes.hasActiveMeeting : ''}`}>
        <div className={classes.header} onClick={handleDeviceClick} style={{ cursor: 'pointer' }}>
            <div className={classes.headerRow}>
                <label>{name}</label>
                {props.allowKioskSettings && (
                    <Tooltip title="Nastavení displeje (object-fit, logo, hodiny…)">
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                setSettingsModalOpen(true);
                            }}
                        >
                            <SettingsIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </div>
            <Divider orientation="horizontal" variant="middle" flexItem/>
        </div>
        <div className={classes.recordsList}>
            {records.map((x, idx) => {
                const from = parseDate(x.dateFrom);
                const to = parseDate(x.dateTo);
                const timeFrom = formatTimeLocal(from);
                const timeTo = formatTimeLocal(to);
                const now = currentTime.getTime();
                const isRunning = !isNaN(from) && !isNaN(to) && from <= now && now <= to;

                // For display id=5 (room=16 flow), overlapping records are allowed
                // and should not be highlighted in red in the table.
                const disableOverlapHighlight = Number(props.id) === 5;
                const isOverlap = disableOverlapHighlight
                    ? false
                    : records.some((r, j) => {
                        if (j === idx) return false;
                        const oFrom = parseDate(r.dateFrom);
                        const oTo = parseDate(r.dateTo);
                        return !isNaN(oFrom) && !isNaN(oTo) && from < oTo && to > oFrom;
                    });
                return (
                    <Record
                        key={x.recordID || x.id}
                        object={x}
                        timeslot={`${timeFrom} - ${timeTo}`}
                        description={x.recordDescription || x.description}
                        room={x.roomDescription}
                        update={update_delete_handler}
                        isOverlap={isOverlap}
                        isRunning={isRunning}
                    />
                );
            })}
            {isLoading && <CircularProgress />}
            {!isLoading && records.length === 0 && <label>No records</label>}
        </div>
        {!isLoading && records.length > 0 && <label className={classes.recordText}>{`${records.length} ${records.length === 1 ? 'record' : 'records'}`}</label>}

        {settingsModalOpen && (
            <EditDisplay_modal
                display={displayForSettings}
                orgId={displayForSettings.cOrganization_id || props.orgId}
                close={() => setSettingsModalOpen(false)}
                allowKioskSettings={props.allowKioskSettings}
            />
        )}
    </div>
}

export default Device